import React, { useContext, useState } from "react"
import { Box, Button, Stack } from "@mui/material"
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined"
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { UserContext, useSnackbar, useCompany } from "src/providers"
import { Community } from "src/models/user"
import { updateCompanyMember, deleteInvitation, resendInvitation, deactivateCompanyMember } from "src/services/api/companyMembers"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import ViewButton from "src/components/table/ViewButton"
import { useDebounce } from "src/utils/useDebounce"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { Role } from "src/UserService"
import { DeactivateMemberDialog } from "./DeactivateMemberDialog"
import { EditMemberModal } from "./EditMemberModal"
import { InviteMemberDialog } from "./InviteMemberDialog"
import { MembersFilterBar } from "./MembersFilterBar"
import { Member, MembersTable } from "./MembersTable"
import {
  useCompanyMembersFetch
} from "./useCompanyMembersFetch"
import { renderCommunitiesCell } from "./companyMembersUtils"
import { StatusChip } from "src/components/table/StatusChip"

export const CompanyMembersTab = () => {
  const { user, userCommunities } = useContext(UserContext)
  const { snackbarShowMessage } = useSnackbar()
  const { companyId } = useCompany()

  const [filters, setFilters] = useLocalStorage<{
    query: string
    communityIds: string[]
    roleIds: string[]
  }>("portal/company-members-filters", { query: "", communityIds: [], roleIds: [] })

  const query = filters.query ?? ""
  const setQuery = (value: string) => setFilters((prev) => ({ ...prev, query: value }))
  const communityIds = filters.communityIds ?? []
  const setCommunityIds = (value: string[]) => setFilters((prev) => ({ ...prev, communityIds: value }))
  const roles = (filters.roleIds ?? []) as Role[]
  const setRoles = (value: Role[]) => setFilters((prev) => ({ ...prev, roleIds: value }))

  const debouncedQuery = useDebounce(query, 400)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [order, setOrder] = useState<"asc" | "desc">("asc")
  const [orderBy, setOrderBy] = useState<"FIRST_NAME" | "LAST_NAME" | "EMAIL" | "PROFILE">("LAST_NAME")
  const [refreshKey, setRefreshKey] = useState(0)
  const [editMember, setEditMember] = useState<Member | null>(null)
  const [deactivateMember, setDeactivateMember] = useState<Member | null>(null)
  const [addModalOpen, setAddModalOpen] = useState(false)

  const communityOptions = userCommunities ?? []
  const selectedCommunities = communityOptions.filter((c) => communityIds.includes(c.gatedCommunityId))

  const { members, totalCount, loading } = useCompanyMembersFetch({
    companyId: companyId ?? undefined,
    page,
    rowsPerPage,
    orderBy,
    order,
    query: debouncedQuery,
    roles,
    communityIds,
    refreshKey,
    userCommunities: communityOptions
  })

  const handleRequestSort = (property: "FIRST_NAME" | "LAST_NAME" | "EMAIL" | "PROFILE") => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const clearFilters = () => {
    setFilters({ query: "", communityIds: [], roleIds: [] })
    setPage(0)
  }

  const handleChangeProfile = (member: Member) => {
    if (!member.userId) return
    const newProfile = member.profile === "MANAGER" ? "MEMBER" : "MANAGER"
    updateCompanyMember(companyId!, member.userId, newProfile)
      .then(() => {
        snackbarShowMessage("Profile updated", "success")
        setRefreshKey((k) => k + 1)
      })
      .catch(() => snackbarShowMessage("Failed to update profile", "error"))
  }

  const communityFilterSlot = (
    <CustomAutocomplete<Community>
      value={selectedCommunities}
      options={communityOptions}
      getOptionLabel={(opt) => opt.gatedCommunityName}
      isOptionEqualToValue={(a, b) => a.gatedCommunityId === b.gatedCommunityId}
      onChange={(_, val) => {
        const arr = (Array.isArray(val) ? val : []) as Community[]
        setCommunityIds(arr.map((c) => c.gatedCommunityId))
      }}
      label="Knowledge Base"
      size="small"
      multiple
    />
  )

  const handleRemoveMember = (member: Member) => {
    setDeactivateMember(member)
  }

  const handleDeactivateConfirm = (member: Member) => {
    if (!member.userId) return
    setDeactivateMember(null)
    const request = member.profile === null
      ? deleteInvitation(member.userId)
      : deactivateCompanyMember(companyId, member.userId)
    request
      .then(() => {
        snackbarShowMessage(member.profile === null ? "Invitation removed" : "Member deactivated", "success")
        setRefreshKey((k) => k + 1)
      })
      .catch(() => snackbarShowMessage(member.profile === null ? "Failed to remove invitation" : "Failed to deactivate member", "error"))
  }

  const handleResendInvitation = (member: Member) => {
    if (!member.userId) return
    resendInvitation(member.userId)
      .then(() => snackbarShowMessage("Invitation resent", "success"))
      .catch(() => snackbarShowMessage("Failed to resend invitation", "error"))
  }

  const rightSlot = (
    <Button
      variant="contained"
      color="primary"
      sx={{ ml: "auto" }}
      onClick={() => setAddModalOpen(true)}
    >
      Invite team member
    </Button>
  )

  return (
    <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <MembersFilterBar
        query={query}
        onQueryChange={setQuery}
        communityFilterSlot={communityFilterSlot}
        selectedRoleIds={roles}
        onRolesChange={setRoles}
        onClearFilters={clearFilters}
        rightSlot={rightSlot}
      />
      <MembersTable
        members={members}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        orderBy={orderBy}
        order={order}
        onRequestSort={handleRequestSort}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showGatedCommunitiesColumn={true}
        renderCommunitiesCell={renderCommunitiesCell}
        renderProfileCell={(member) => {
          return (
            member.profile === "MANAGER" ? (
              <Box component="span" fontWeight="bold">Manager</Box>
            ) : member.isPending ? (
              <StatusChip label="PENDING" />
            ) : (
              "Member"
            )
          )
        }
        }
        renderActions={(member) => (
          member.isPending ? (
            <Stack spacing={0.5} alignItems="left">
              <Button size="small" variant="outlined" color="secondary" onClick={() => handleResendInvitation(member)}>Resend invitation</Button>
              <ViewButton size="small" aria-label="Remove member" onClick={() => handleRemoveMember(member)}
                sx={{ width: "fit-content" }}>
                <PersonOffOutlinedIcon fontSize="small" />
              </ViewButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ViewButton size="small" aria-label="Edit member" onClick={() => handleRemoveMember(member)}
                disabled={!!(user?.email && member.email && user.email === member.email)}>
                <PersonOffOutlinedIcon fontSize="small" />
              </ViewButton>
              <ViewButton
                size="small"
                aria-label="Change profile"
                onClick={() => handleChangeProfile(member)}
                disabled={!!(user?.email && member.email && user.email === member.email)}
              >
                {member.profile === "MANAGER" ? (
                  <ArrowDownwardOutlinedIcon fontSize="small" />
                ) : (
                  <ArrowUpwardOutlinedIcon fontSize="small" />
                )}
              </ViewButton>
              <ViewButton size="small" aria-label="Edit member" onClick={() => setEditMember(member)}>
                <EditOutlinedIcon fontSize="small" />
              </ViewButton>
            </Stack>
          )
        )}
        tableAriaLabel="Team table"
      />
      <DeactivateMemberDialog
        member={deactivateMember}
        title={deactivateMember?.isPending ? "Remove Invitation?" : "Deactivate Member Account?"}
        bodyText={deactivateMember?.isPending ? (
          <>Are you sure you want to remove the invitation for <strong>{deactivateMember?.email}</strong>?</>
        ) : (
          <>Are you sure you want to deactivate <strong>{deactivateMember?.firstName} {deactivateMember?.lastName}</strong> account?</>
        )}
        buttonText={deactivateMember?.isPending ? "Yes, remove invitation" : "Yes, deactivate member"}
        onConfirm={handleDeactivateConfirm}
        onClose={() => setDeactivateMember(null)}
      />
      {editMember && <EditMemberModal
        open={editMember !== null}
        member={editMember}
        onClose={() => setEditMember(null)}
        onUpdated={() => setRefreshKey((k) => k + 1)}
      />
      }
      <InviteMemberDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onInvited={() => setRefreshKey((k) => k + 1)}
        teamId={companyId ?? ""}
        communityOptions={communityOptions}
      />
    </Stack>
  )
}
